import { useMemo } from 'react';

import { Typography } from '@mui/material';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';

import { getState } from 'state/store';
import { getSearchParamsForState } from 'state/workflowSlice';

import { StlCard } from 'appTypes';
import { pathForSearchParams } from 'appUtils';
import { useFilteredArrayOfObjects } from 'features/common/hooks/useFilteredArrayOfObjects';
import LoadingDotAnimation from 'features/spinner/LoadingDotAnimation';

import { classNames, deepClone } from '../utils';

import styles from './StlList.module.css';

interface IStlList<T> {
  filters?: { [key: string]: boolean | string };
  wide?: boolean;
  onItemClick: (event: React.MouseEvent, item: T) => void;
  currentItemId?: T[keyof T];
  filterBy?: Extract<keyof T, string>;
  limit?: number;
  highlightSelected?: boolean;
  editing?: boolean;
  idAccessor: keyof T;
  CardComponent: React.ComponentType<StlCard<T>>;
  data: T[];
  loading?: boolean;
  itemName: string;
  classes?: string[];
  selectedOnly?: boolean;
  excludeSelected?: boolean;
}

export function StlList<T>({
  filters,
  wide = false,
  onItemClick,
  currentItemId,
  filterBy,
  highlightSelected,
  limit,
  editing = false,
  idAccessor,
  CardComponent,
  data,
  loading = false,
  itemName,
  classes = [],
  selectedOnly = false,
  excludeSelected = false,
}: IStlList<T>) {
  const filtersMemo = useMemo(() => filters ?? {}, [filters]);
  const filtered = useFilteredArrayOfObjects(
    data,
    undefined,
    filtersMemo,
    filterBy,
    -1,
    undefined,
    true,
  ); // there's room for more optimized solution

  const items = useMemo(() => {
    let arr = filtered ?? [];
    if (filtered && filtered.length > 0 && editing && currentItemId) {
      arr = arr.filter((el) => el[idAccessor] !== currentItemId);
    }

    if (selectedOnly) {
      if (!currentItemId) return [];
      const selected = arr.find((el) => el[idAccessor] === currentItemId);
      return selected ? [selected] : [];
    }

    if (excludeSelected && currentItemId) {
      return arr.filter((el) => el[idAccessor] !== currentItemId);
    }

    // put current item first if it exists
    if (!currentItemId) return arr;

    const selected = arr.find((el) => el[idAccessor] === currentItemId);
    if (!selected) return arr;

    return [selected, ...arr.filter((el) => el[idAccessor] !== currentItemId)];
  }, [
    filtered,
    editing,
    currentItemId,
    selectedOnly,
    excludeSelected,
    idAccessor,
  ]);

  const handleAuxClick = (item: T) => {
    const baseState = deepClone(getState().workflow);
    currentItemId = item[idAccessor];
    const searchParams = getSearchParamsForState(baseState);
    const url = pathForSearchParams(searchParams);
    window.open(url, '_blank');
  };

  let listElement = (
    <div className={styles.loading_wrapper}>
      <LoadingDotAnimation />
    </div>
  );

  function renderListItem(item: T) {
    const selected = item[idAccessor] === currentItemId;

    return (
      <ListItem
        key={`${item[idAccessor]}_${wide && 'wide'}`}
        onClick={(event) => onItemClick(event, item)}
        className={styles.list_item}
        onMouseDown={
          selected
            ? undefined
            : (ev) => {
                if (ev.button === 1) {
                  // Button 1 == Middle mouse button
                  handleAuxClick(item);
                }
              }
        }
      >
        <CardComponent
          fullWidth={wide}
          item={item}
          selected={selected && highlightSelected}
        />
      </ListItem>
    );
  }
  if (!loading && filtered?.length === 0 && data?.length > 0)
    listElement = <Typography variant="body2">No {itemName} found</Typography>;
  else if (!loading) {
    listElement = (
      <List
        dense
        classes={{
          root: selectedOnly ? styles.list_selected : styles.list,
        }}
      >
        {items?.slice(0, limit || filtered?.length).map(renderListItem)}
      </List>
    );
  }

  const containerClass = wide
    ? styles.list_container_full_width
    : selectedOnly
      ? styles.selected_container
      : styles.list_container;

  return (
    <div className={classNames(containerClass, ...classes)}>{listElement}</div>
  );
}
